import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import studio from "../../styles/studioPage.module.css";
import adminSurface from "../../components/layouts/AdminSurface.module.css";
import TitleSubtitle from "../../components/common/TitleSubtitle";
import TextInput from "../../components/ui/TextInput";
import Icon from "../../components/common/Icon";
import { ModalPortal } from "../../components/modals/ModalPortal";
import { createPrompt, getPrompts, updatePrompt } from "../../api/administration";
import { Prompt } from "../../types/types";

const Prompts: React.FC = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const [prompts, setPrompts] = useState<Prompt[]>([]);
    const [editedPrompt, setEditedPrompt] = useState<Prompt | null>(null);
    const [isNewPrompt, setIsNewPrompt] = useState<boolean>(false);
    const [search, setSearch] = useState<string>("");

    const getPromptsAPI = async () => {
        const token = localStorage.getItem("token");
        if (!token) return;
        const promptsAPI = await getPrompts({ token });
        if (!promptsAPI) {
            console.error("Failed to fetch prompts");
        } else {
            setPrompts(promptsAPI);
        }
    };

    const createPromptAPI = async (prompt: Prompt) => {
        const token = localStorage.getItem("token");
        if (!token) return;
        await createPrompt({
            token,
            id: prompt.id,
            internalName: prompt.internalName,
            prompt: prompt.prompt,
        });
        await getPromptsAPI();
    };

    const updatePromptAPI = async (prompt: Prompt) => {
        const token = localStorage.getItem("token");
        if (!token) return;
        await updatePrompt({
            token,
            id: prompt.id,
            internalName: prompt.internalName,
            prompt: prompt.prompt,
        });
        await getPromptsAPI();
    };

    useEffect(() => {
        getPromptsAPI();
    }, []);

    const handleSelectPrompt = (prompt: Prompt) => {
        setEditedPrompt({ ...prompt });
        setIsNewPrompt(false);
    };

    const handleNewPrompt = () => {
        setEditedPrompt({ id: "", internalName: "", prompt: "" });
        setIsNewPrompt(true);
    };

    const handleSave = async () => {
        if (editedPrompt) {
            if (!prompts.find((p) => p.id === editedPrompt.id)) {
                await createPromptAPI(editedPrompt);
            } else {
                await updatePromptAPI(editedPrompt);
            }
            setEditedPrompt(null);
            setIsNewPrompt(false);
        }
    };

    const handleCancel = () => {
        setEditedPrompt(null);
        setIsNewPrompt(false);
    };

    const filteredPrompts = useMemo(
        () =>
            prompts.filter(
                (prompt) =>
                    prompt.id.toLowerCase().includes(search.toLowerCase()) ||
                    prompt.internalName.toLowerCase().includes(search.toLowerCase()) ||
                    prompt.prompt.toLowerCase().includes(search.toLowerCase()),
            ),
        [prompts, search],
    );

    const modalTitle = isNewPrompt
        ? t("createNewPrompt")
        : `${t("editPrompt")}${editedPrompt?.internalName ? ` — ${editedPrompt.internalName}` : ""}`;

    return (
        <div className={adminSurface.pageContent}>
            <div className={`${studio.pageTitle} ${adminSurface.pageTitle}`}>
                <TitleSubtitle title={t("prompts")} subtitle={t("promptsSubTitle")} />
            </div>

            <div className={adminSurface.tblBar}>
                <div className={adminSurface.searchWrap}>
                    <Icon name="ic-search" className={adminSurface.searchWrapIcon} size={18} />
                    <TextInput
                        customClassName={adminSurface.searchInput}
                        placeholder={t("searchPrompt")}
                        onChange={(e) => setSearch(e.target.value)}
                        value={search}
                        disabled={false}
                    />
                </div>
                <button
                    type="button"
                    className={`${adminSurface.addButton} ${adminSurface.tblBarEnd}`}
                    onClick={handleNewPrompt}
                >
                    <Icon name="ic-plus" className="ic" size={16} />
                    {t("newPrompt")}
                </button>
            </div>

            <div className={adminSurface.tableCard}>
                <div className={adminSurface.tblScrollBody}>
                    <table className={adminSurface.table}>
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>{t("internalName")}</th>
                                <th>{t("promptPreview")}</th>
                                <th className={adminSurface.colAct}>{t("actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPrompts.map((prompt) => (
                                <tr key={prompt.id}>
                                    <td style={{ fontFamily: "monospace", fontSize: "12.5px" }}>{prompt.id}</td>
                                    <td style={{ fontWeight: 600 }}>{prompt.internalName}</td>
                                    <td style={{ color: "var(--ink-3)", fontSize: "13.5px", maxWidth: "360px" }}>
                                        {prompt.prompt.length > 50
                                            ? prompt.prompt.substring(0, 50) + "..."
                                            : prompt.prompt}
                                    </td>
                                    <td className={adminSurface.colAct}>
                                        <button
                                            className={adminSurface.modifyButton}
                                            onClick={() => handleSelectPrompt(prompt)}
                                        >
                                            {t("edit")}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className={adminSurface.tblFoot}>
                    <span>
                        {filteredPrompts.length} {t("prompts")?.toLowerCase?.() || "prompts"}
                    </span>
                </div>
            </div>

            {editedPrompt && (
                <ModalPortal>
                    <div
                        className={adminSurface.evOv}
                        onClick={(e) => {
                            if (e.target === e.currentTarget) handleCancel();
                        }}
                        role="presentation"
                    >
                        <div
                            className={adminSurface.evModal}
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="prompt-edit-title"
                        >
                            <div className={adminSurface.evHead}>
                                <h2 id="prompt-edit-title">{modalTitle}</h2>
                                <button
                                    type="button"
                                    className={adminSurface.evClose}
                                    onClick={handleCancel}
                                    aria-label={t("close")}
                                >
                                    <Icon name="ic-x" className="ic" size={19} />
                                </button>
                            </div>
                            <div className={adminSurface.evBody}>
                                <div className={adminSurface.evField}>
                                    <label className={adminSurface.evFieldLabel}>ID</label>
                                    <TextInput
                                        type="text"
                                        value={editedPrompt.id}
                                        onChange={(e) => setEditedPrompt({ ...editedPrompt, id: e.target.value })}
                                        placeholder={t("enterPromptId")}
                                        disabled={!isNewPrompt}
                                    />
                                </div>

                                <div className={adminSurface.evField}>
                                    <label className={adminSurface.evFieldLabel}>{t("internalName")}</label>
                                    <TextInput
                                        type="text"
                                        value={editedPrompt.internalName}
                                        onChange={(e) =>
                                            setEditedPrompt({
                                                ...editedPrompt,
                                                internalName: e.target.value,
                                            })
                                        }
                                        placeholder={t("enterInternalName")}
                                    />
                                </div>

                                <div className={adminSurface.evField}>
                                    <label className={adminSurface.evFieldLabel}>{t("promptLabel")}</label>
                                    <textarea
                                        className={adminSurface.evTextarea}
                                        value={editedPrompt.prompt}
                                        onChange={(e) => setEditedPrompt({ ...editedPrompt, prompt: e.target.value })}
                                        placeholder={t("enterPromptText")}
                                    />
                                    <p className={adminSurface.evFieldHelp}>{t("promptVariablesHelp")}</p>
                                </div>
                            </div>
                            <div className={adminSurface.evFoot}>
                                <button type="button" className={adminSurface.btnGhost} onClick={handleCancel}>
                                    {t("close")}
                                </button>
                                <button type="button" className={adminSurface.addButton} onClick={handleSave}>
                                    {isNewPrompt ? t("save") : t("update")}
                                </button>
                            </div>
                        </div>
                    </div>
                </ModalPortal>
            )}
        </div>
    );
};

export default Prompts;
